import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Check, Copy, Webhook, RefreshCw, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ResearchManager({ research, researchRequests, onRefresh, headers, settings }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [formData, setFormData] = useState({ title: '', excerpt: '', full_content: '', doc_url: '', published: false });
    const [showRequests, setShowRequests] = useState(false);
    const [webhookUrl, setWebhookUrl] = useState(settings?.research_webhook_url || '');
    const [savingWebhook, setSavingWebhook] = useState(false);
    const [testingWebhook, setTestingWebhook] = useState(false);
    const [sendingWebhook, setSendingWebhook] = useState(null);

    useEffect(() => {
        setWebhookUrl(settings?.research_webhook_url || '');
    }, [settings]);

    const handleSave = async () => {
        try {
            if (editItem) {
                await axios.put(`${API}/research/${editItem.id}`, formData, { headers });
                toast.success('Research updated!');
            } else {
                await axios.post(`${API}/research`, formData, { headers });
                toast.success('Research created!');
            }
            setDialogOpen(false);
            setEditItem(null);
            setFormData({ title: '', excerpt: '', full_content: '', doc_url: '', published: false });
            onRefresh();
        } catch (error) {
            toast.error('Failed to save');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this research?')) return;
        try {
            await axios.delete(`${API}/research/${id}`, { headers });
            toast.success('Research deleted!');
            onRefresh();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const handleDeleteRequest = async (id) => {
        if (!window.confirm('Delete this access request?')) return;
        try {
            await axios.delete(`${API}/research-requests/${id}`, { headers });
            toast.success('Request deleted!');
            onRefresh();
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const handleSaveWebhook = async () => {
        setSavingWebhook(true);
        try {
            await axios.put(`${API}/settings`, { research_webhook_url: webhookUrl }, { headers });
            toast.success('Research webhook URL saved!');
            onRefresh();
        } catch (error) {
            toast.error('Failed to save webhook URL');
        }
        setSavingWebhook(false);
    };

    const handleTestWebhook = async () => {
        if (!webhookUrl) {
            toast.error('Please enter a webhook URL first');
            return;
        }
        setTestingWebhook(true);
        try {
            // Save first, then test
            await axios.put(`${API}/settings`, { research_webhook_url: webhookUrl }, { headers });
            const res = await axios.post(`${API}/research/webhook/test`, {}, { headers });
            if (res.data.success) {
                toast.success('Research webhook connected successfully!');
            } else {
                toast.error(`Webhook test failed: ${res.data.error || 'Unknown error'}`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to test webhook');
        }
        setTestingWebhook(false);
    };

    const handleSendWebhook = async (requestId) => {
        setSendingWebhook(requestId);
        try {
            const res = await axios.post(`${API}/research-requests/${requestId}/send-webhook`, {}, { headers });
            if (res.data.success) {
                toast.success(`Webhook sent! (Total: ${res.data.sent_count} times)`);
                onRefresh();
            } else {
                toast.error(`Failed: ${res.data.error}`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send webhook');
        }
        setSendingWebhook(null);
    };

    const openEdit = (item) => {
        setEditItem(item);
        setFormData({ 
            title: item.title, 
            excerpt: item.excerpt || '', 
            full_content: item.full_content || '',
            doc_url: item.doc_url || '',
            published: item.published 
        });
        setDialogOpen(true);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            {/* Webhook Configuration */}
            <div className="retro-card p-6" data-testid="research-webhook-config">
                <div className="flex items-center gap-2 mb-4">
                    <Webhook className="h-5 w-5 text-primary" />
                    <h2 className="text-lg uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                        Research Webhook
                    </h2>
                </div>
                <p className="text-xs text-muted-foreground mb-4">
                    When someone requests access to research, this webhook will automatically fire with their details and the research document link.
                </p>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs uppercase tracking-widest">n8n Webhook URL for Research Requests</Label>
                        <Input 
                            className="rounded-none border-2 mt-1" 
                            value={webhookUrl} 
                            onChange={(e) => setWebhookUrl(e.target.value)}
                            placeholder="https://your-n8n-instance.com/webhook/research..."
                            data-testid="research-webhook-url"
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button 
                            onClick={handleSaveWebhook}
                            disabled={savingWebhook}
                            className="rounded-none border-2 border-primary bg-primary text-primary-foreground text-xs uppercase tracking-widest"
                            data-testid="save-research-webhook-btn"
                        >
                            {savingWebhook ? 'Saving...' : 'Save Webhook'}
                        </Button>
                        <Button 
                            onClick={handleTestWebhook}
                            disabled={testingWebhook || !webhookUrl}
                            variant="outline"
                            className="rounded-none border-2 text-xs uppercase tracking-widest"
                            data-testid="test-research-webhook-btn"
                        >
                            <RefreshCw className={`h-4 w-4 mr-2 ${testingWebhook ? 'animate-spin' : ''}`} />
                            {testingWebhook ? 'Testing...' : 'Test Connection'}
                        </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                        Payload includes: requester name, email, phone, research title, excerpt, and document URL
                    </p>
                </div>
            </div>

            {/* Research CRUD */}
            <div className="retro-card p-6" data-testid="research-manager">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                        Manage Research
                    </h2>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button className="rounded-none border-2 border-primary bg-primary text-primary-foreground retro-btn text-xs uppercase tracking-widest" data-testid="add-research-btn">
                                <Plus className="h-4 w-4 mr-2" />
                                Add Research
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-none border-2 max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                                    {editItem ? 'Edit Research' : 'New Research'}
                                </DialogTitle>
                                <DialogDescription>Add research with an excerpt preview. Users must request access to view full content.</DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-widest">Title</Label>
                                    <Input className="rounded-none border-2 mt-1" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} data-testid="research-title-input" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest">Excerpt (Preview shown to users)</Label>
                                    <Textarea className="rounded-none border-2 mt-1 min-h-[150px]" value={formData.excerpt} onChange={(e) => setFormData({...formData, excerpt: e.target.value})} placeholder="Write the preview excerpt that users will see before requesting access..." data-testid="research-excerpt-input" />
                                    <p className="text-xs text-muted-foreground mt-1">This is what users see before they submit their details to request full access</p>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest">Full Content (Optional - for your reference)</Label>
                                    <Textarea className="rounded-none border-2 mt-1 min-h-[100px]" value={formData.full_content} onChange={(e) => setFormData({...formData, full_content: e.target.value})} placeholder="Full research content or notes..." data-testid="research-content-input" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-widest">Document URL (Sent via webhook)</Label>
                                    <Input className="rounded-none border-2 mt-1" value={formData.doc_url} onChange={(e) => setFormData({...formData, doc_url: e.target.value})} placeholder="https://docs.google.com/..." data-testid="research-url-input" />
                                    <p className="text-xs text-muted-foreground mt-1">This link will be included in the webhook payload for your n8n flow to send to requesters</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Switch checked={formData.published} onCheckedChange={(checked) => setFormData({...formData, published: checked})} data-testid="research-published-switch" />
                                    <Label className="text-xs uppercase tracking-widest">Published</Label>
                                </div>
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => { setDialogOpen(false); setEditItem(null); }} className="rounded-none border-2">Cancel</Button>
                                <Button onClick={handleSave} className="rounded-none border-2 border-primary bg-primary text-primary-foreground" data-testid="save-research-btn">Save</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                </div>

                {research.length === 0 ? (
                    <p className="text-muted-foreground text-center py-8">No research yet. Add your first one!</p>
                ) : (
                    <div className="space-y-4">
                        {research.map(item => (
                            <div key={item.id} className="flex items-center justify-between p-4 border-2 border-border" data-testid={`research-row-${item.id}`}>
                                <div className="flex-1">
                                    <h3 className="font-bold">{item.title}</h3>
                                    <p className="text-sm text-muted-foreground line-clamp-2">{item.excerpt}</p>
                                    {item.doc_url && <p className="text-xs text-primary mt-1 truncate">{item.doc_url}</p>}
                                </div>
                                <div className="flex items-center gap-2">
                                    {item.published ? (
                                        <span className="text-xs uppercase tracking-widest text-green-500 flex items-center gap-1"><Check className="h-3 w-3" /> Live</span>
                                    ) : (
                                        <span className="text-xs uppercase tracking-widest text-muted-foreground">Draft</span>
                                    )}
                                    <Button variant="outline" size="icon" onClick={() => openEdit(item)} className="rounded-none border-2" data-testid={`edit-research-${item.id}`}><Edit className="h-4 w-4" /></Button>
                                    <Button variant="outline" size="icon" onClick={() => handleDelete(item.id)} className="rounded-none border-2 text-destructive" data-testid={`delete-research-${item.id}`}><Trash2 className="h-4 w-4" /></Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Access Requests */}
            <div className="retro-card p-6" data-testid="research-requests-manager">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="text-lg uppercase tracking-tight" style={{ fontFamily: "'Silkscreen', cursive" }}>
                            Access Requests
                        </h2>
                        <p className="text-xs text-muted-foreground">{researchRequests.length} pending requests</p>
                    </div>
                    <Button 
                        variant="outline" 
                        onClick={() => setShowRequests(!showRequests)}
                        className="rounded-none border-2 text-xs uppercase"
                    >
                        {showRequests ? 'Hide' : 'Show'} Requests
                    </Button>
                </div>

                {showRequests && (
                    researchRequests.length === 0 ? (
                        <p className="text-muted-foreground text-center py-4">No access requests yet.</p>
                    ) : (
                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                            {researchRequests.map(req => (
                                <div key={req.id} className="p-3 border-2 border-border bg-muted/30">
                                    <div className="flex justify-between items-start">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-bold text-sm">{req.name}</p>
                                                {req.webhook_sent_count > 0 && (
                                                    <span className="text-[10px] px-1.5 py-0.5 bg-green-500/20 text-green-600 border border-green-500/30">
                                                        Sent {req.webhook_sent_count}x
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-primary">{req.email}</p>
                                            {req.phone && <p className="text-xs text-muted-foreground">WhatsApp: {req.phone}</p>}
                                            <p className="text-xs text-muted-foreground mt-1">For: <span className="text-foreground">{req.research_title || 'Unknown Research'}</span></p>
                                            <p className="text-[10px] text-muted-foreground">{formatDate(req.created_at)}</p>
                                            {req.last_webhook_sent && (
                                                <p className="text-[10px] text-green-600">Last sent: {formatDate(req.last_webhook_sent)}</p>
                                            )}
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => handleSendWebhook(req.id)}
                                                disabled={sendingWebhook === req.id || !webhookUrl}
                                                className="rounded-none border-2 h-7 text-xs"
                                                data-testid={`send-webhook-${req.id}`}
                                            >
                                                {sendingWebhook === req.id ? (
                                                    <RefreshCw className="h-3 w-3 animate-spin" />
                                                ) : (
                                                    <>
                                                        <Send className="h-3 w-3 mr-1" />
                                                        Send
                                                    </>
                                                )}
                                            </Button>
                                            <div className="flex gap-1">
                                                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(req.email); toast.success('Email copied!'); }} className="rounded-none border-2 h-7 w-7">
                                                    <Copy className="h-3 w-3" />
                                                </Button>
                                                <Button variant="outline" size="icon" onClick={() => handleDeleteRequest(req.id)} className="rounded-none border-2 text-destructive h-7 w-7">
                                                    <Trash2 className="h-3 w-3" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )
                )}
            </div>
        </div>
    );
}
